import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import type { EntityDef, EntityFieldDef } from "./entitySchema";
import { bindingKey, rankPathForEntity } from "./mappingUtils";

interface MappingPanelProps {
  entitySchema: EntityDef[];
  flattenedPaths: string[];
  iterationRules: StudioIterationRule[];
  bindings: Record<string, string>;
  pathPreviews: Record<string, string>;
  sourceName: string;
  supportedSources: SourceMessage[];
  onSourceChange: (sourceName: string) => void;
  onBindingChange: (entity: string, field: string, path: string | null) => void;
}

const LIKELY_GROUP = "Likely matches";
const OTHER_GROUP = "Other paths";

function iterationLabel(rule: StudioIterationRule): string {
  return rule.parentEntity ? `× per ${rule.parentEntity}` : `× repeats`;
}

interface FieldRowProps {
  entity: EntityDef;
  field: EntityFieldDef;
  value: string | null;
  entityHasAnyBinding: boolean;
  sortedPaths: string[];
  iterationRules: StudioIterationRule[];
  pathPreviews: Record<string, string>;
  onBindingChange: (entity: string, field: string, path: string | null) => void;
  // Present only for optional fields (required fields can't be removed).
  // Click clears the binding and hides the row.
  onRemove?: (entity: string, field: string) => void;
}

function FieldRow({
  entity,
  field,
  value,
  entityHasAnyBinding,
  sortedPaths,
  iterationRules,
  pathPreviews,
  onBindingChange,
  onRemove,
}: FieldRowProps) {
  const isMissingRequired = field.required && entityHasAnyBinding && !value;
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box sx={{ width: 200, flexShrink: 0, paddingTop: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {field.label}
          </Typography>
          {field.required && (
            <Typography
              variant="body2"
              sx={{
                color: isMissingRequired ? "error.main" : "text.secondary",
                fontWeight: 600,
              }}
            >
              *
            </Typography>
          )}
        </Stack>
        {field.type && (
          <Typography variant="caption" color="text.secondary">
            {field.type}
          </Typography>
        )}
        {!value && field.defaultValue && !isMissingRequired && (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              fontStyle: "italic",
            }}
          >
            → default: {field.defaultValue}
          </Typography>
        )}
        {isMissingRequired && (
          <Typography
            variant="caption"
            sx={{ color: "error.main", display: "block" }}
          >
            Required
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Autocomplete
          freeSolo
          size="small"
          fullWidth
          options={sortedPaths}
          value={value}
          groupBy={(option) =>
            rankPathForEntity(option, entity.name, iterationRules) >= 70
              ? LIKELY_GROUP
              : OTHER_GROUP
          }
          renderGroup={(params) => (
            <li key={params.key}>
              <Box
                sx={{
                  padding: "4px 16px",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color:
                    params.group === LIKELY_GROUP
                      ? "primary.main"
                      : "text.secondary",
                  backgroundColor:
                    params.group === LIKELY_GROUP ? "#EDE9FE" : "#F5F5F5",
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                {params.group}
              </Box>
              <Box
                component="ul"
                sx={{
                  padding: 0,
                  backgroundColor:
                    params.group === LIKELY_GROUP ? "#FBFAFF" : "transparent",
                }}
              >
                {params.children}
              </Box>
            </li>
          )}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: "0.8125rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {option}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {pathPreviews[option] ?? "—"}
                </Typography>
              </Box>
            </li>
          )}
          onChange={(_e, newValue) =>
            onBindingChange(
              entity.name,
              field.name,
              (newValue as string) || null,
            )
          }
          onInputChange={(_e, newInput, reason) => {
            if (reason === "clear") {
              onBindingChange(entity.name, field.name, null);
            } else if (reason === "input") {
              onBindingChange(entity.name, field.name, newInput || null);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select or type a JSON path"
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: "0.8125rem",
                },
              }}
            />
          )}
        />
        {value && pathPreviews[value] && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              marginTop: 0.25,
              paddingLeft: 1.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            → {pathPreviews[value]}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, width: 28, paddingTop: 0.5 }}>
        {onRemove && (
          <Tooltip title="Remove this field from the mapping">
            <IconButton
              size="small"
              onClick={() => onRemove(entity.name, field.name)}
              sx={{ padding: 0.5, color: "text.secondary" }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Stack>
  );
}

export default function MappingPanel({
  entitySchema,
  flattenedPaths,
  iterationRules,
  bindings,
  pathPreviews,
  sourceName,
  supportedSources,
  onSourceChange,
  onBindingChange,
}: MappingPanelProps) {
  const [addedFields, setAddedFields] = useState<Record<string, Set<string>>>(
    {},
  );
  const prevBindingCountRef = useRef<number>(Object.keys(bindings).length);
  useEffect(() => {
    const count = Object.keys(bindings).length;
    if (count === 0 && prevBindingCountRef.current > 0) {
      setAddedFields({});
    }
    prevBindingCountRef.current = count;
  }, [bindings]);

  const addField = (entityName: string, fieldName: string) => {
    setAddedFields((prev) => {
      const next = { ...prev };
      const set = new Set(next[entityName] ?? []);
      set.add(fieldName);
      next[entityName] = set;
      return next;
    });
  };

  const removeField = (entityName: string, fieldName: string) => {
    setAddedFields((prev) => {
      const set = prev[entityName];
      if (!set?.has(fieldName)) return prev;
      const nextSet = new Set(set);
      nextSet.delete(fieldName);
      return { ...prev, [entityName]: nextSet };
    });
    onBindingChange(entityName, fieldName, null);
  };

  const sortedPathsByEntity: Record<string, string[]> = {};
  entitySchema.forEach((entity) => {
    sortedPathsByEntity[entity.name] = [...flattenedPaths].sort((a, b) => {
      const ra = rankPathForEntity(a, entity.name, iterationRules);
      const rb = rankPathForEntity(b, entity.name, iterationRules);
      if (ra !== rb) return rb - ra;
      return a.localeCompare(b);
    });
  });

  const payerSources = supportedSources.filter((s) => s.sourceType === "PAYER");

  return (
    <Box sx={{ padding: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          padding: 2,
          marginBottom: 2,
          backgroundColor: !sourceName ? "#FFF8E1" : "#FFFFFF",
          borderColor: !sourceName ? "warning.light" : "divider",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}
        >
          SOURCE *
        </Typography>
        <Select
          value={sourceName}
          onChange={(e) => onSourceChange(e.target.value)}
          size="small"
          displayEmpty
          fullWidth
          sx={{ marginTop: 0.5 }}
        >
          <MenuItem value="" disabled>
            Select a source
          </MenuItem>
          {payerSources.map((s) => (
            <MenuItem key={s.id} value={s.name}>
              {s.label || s.name}
            </MenuItem>
          ))}
        </Select>
        {!sourceName && (
          <Typography
            variant="caption"
            sx={{ color: "warning.dark", display: "block", marginTop: 0.5 }}
          >
            Source is required to save the template.
          </Typography>
        )}
      </Paper>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ marginBottom: 2 }}
      >
        Required fields are shown by default. Use &quot;+ Add field&quot; to
        surface other fields when your document has them.
      </Typography>

      <Box sx={{ marginBottom: 2 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          DETECTED ITERATIONS
        </Typography>
        {iterationRules.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginTop: 0.5 }}
          >
            No repeating sections detected.
          </Typography>
        ) : (
          <Stack
            direction="row"
            spacing={1}
            sx={{ marginTop: 0.5, flexWrap: "wrap", gap: 0.5 }}
          >
            {iterationRules.map((r) => (
              <Chip
                key={`${r.entity}-${r.jsonPath}`}
                size="small"
                label={`${r.entity} ${iterationLabel(r)} · ${r.jsonPath}`}
                variant="outlined"
              />
            ))}
          </Stack>
        )}
      </Box>

      {entitySchema.map((entity) => {
        const added = addedFields[entity.name] ?? new Set<string>();
        const requiredFields = entity.fields.filter((f) => f.required);
        const optionalSurfaced = entity.fields.filter(
          (f) =>
            !f.required &&
            (bindings[bindingKey(entity.name, f.name)] || added.has(f.name)),
        );
        const pool = entity.fields.filter(
          (f) =>
            !f.required &&
            !bindings[bindingKey(entity.name, f.name)] &&
            !added.has(f.name),
        );

        const entityHasAnyBinding = entity.fields.some(
          (f) => bindings[bindingKey(entity.name, f.name)],
        );
        const boundCount = entity.fields.filter(
          (f) => bindings[bindingKey(entity.name, f.name)],
        ).length;
        const surfacedCount = requiredFields.length + optionalSurfaced.length;

        return (
          <Accordion
            key={entity.name}
            defaultExpanded={boundCount > 0 || entity.name === "Claim"}
            disableGutters
            sx={{
              marginBottom: 1,
              "&:before": { display: "none" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontWeight: 600 }}>{entity.label}</Typography>
                <Chip
                  size="small"
                  label={`${boundCount}/${surfacedCount}`}
                  color={boundCount > 0 ? "primary" : "default"}
                  variant={
                    boundCount === surfacedCount && surfacedCount > 0
                      ? "filled"
                      : "outlined"
                  }
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {requiredFields.map((field) => (
                  <FieldRow
                    key={bindingKey(entity.name, field.name)}
                    entity={entity}
                    field={field}
                    value={
                      bindings[bindingKey(entity.name, field.name)] ?? null
                    }
                    entityHasAnyBinding={entityHasAnyBinding}
                    sortedPaths={sortedPathsByEntity[entity.name] ?? []}
                    iterationRules={iterationRules}
                    pathPreviews={pathPreviews}
                    onBindingChange={onBindingChange}
                  />
                ))}
                {optionalSurfaced.map((field) => (
                  <FieldRow
                    key={bindingKey(entity.name, field.name)}
                    entity={entity}
                    field={field}
                    value={
                      bindings[bindingKey(entity.name, field.name)] ?? null
                    }
                    entityHasAnyBinding={entityHasAnyBinding}
                    sortedPaths={sortedPathsByEntity[entity.name] ?? []}
                    iterationRules={iterationRules}
                    pathPreviews={pathPreviews}
                    onBindingChange={onBindingChange}
                    onRemove={removeField}
                  />
                ))}
                {pool.length > 0 && (
                  <Box>
                    <Autocomplete
                      size="small"
                      options={pool}
                      getOptionLabel={(o) => o.label}
                      value={null}
                      blurOnSelect
                      clearOnBlur
                      sx={{ maxWidth: 320 }}
                      onChange={(_e, picked) => {
                        if (picked) addField(entity.name, picked.name);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="+ Add field"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontFamily: '"Roboto Mono", monospace',
                              fontSize: "0.8125rem",
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.name}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <AddIcon fontSize="small" color="action" />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: '"Roboto Mono", monospace',
                                fontSize: "0.8125rem",
                              }}
                            >
                              {option.label}
                            </Typography>
                            {option.type && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.type}
                              </Typography>
                            )}
                          </Stack>
                        </li>
                      )}
                    />
                  </Box>
                )}
                {pool.length === 0 &&
                  requiredFields.length + optionalSurfaced.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No fields defined for {entity.label}.
                    </Typography>
                  )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
